import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { authApi } from '../api/endpoints/auth';
import type { User } from '../types/auth';
import { isDemoMode } from '../lib/demoMode';
import { demoFixtures } from '../lib/demoData';
import { openAuthPrompt } from '../lib/authPrompt';
import { isNative } from '../lib/platform';
import { setHasSession } from '../lib/sessionFlag';
import { registerPushNotifications, unregisterPushNotifications } from '../lib/pushNotifications';
import { queryClient } from '../lib/queryClient';
import { confirmPendingAuthMethod } from '../lib/lastAuthMethod';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isDemo: boolean;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthState>({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  isDemo: false,
  logout: () => {},
  refreshUser: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const demo = typeof window !== 'undefined' && isDemoMode();

  useEffect(() => {
    if (demo) {
      setUser(demoFixtures.user as unknown as User);
      setHasSession(true);
      setIsLoading(false);
      return;
    }

    authApi
      .me()
      .then((u) => {
        setUser(u);
        setHasSession(true);
        setIsLoading(false);
        // A social sign-in redirects away before we can confirm it worked, so
        // the click only recorded a pending method. Booting with a live
        // session is that confirmation.
        confirmPendingAuthMethod();
        // Fire-and-forget — push registration must not block render or the
        // session bootstrap. No-op on web.
        registerPushNotifications();
      })
      .catch(() => {
        setHasSession(false);
        setIsLoading(false);
        // On native, MobileWelcome handles the unauthenticated state; auto-
        // popping a modal over a blank screen at launch feels jarring.
        if (!isNative) openAuthPrompt('signin');
      });
  }, [demo]);

  const logout = async () => {
    setHasSession(false);
    // Best-effort: tell the backend to stop sending pushes to this device,
    // then tear down listeners.
    await unregisterPushNotifications();
    // Revoke the session + clear the cookie on our own backend. No 00acc
    // round-trip — we never bounce the user to an external origin.
    try {
      await authApi.signout();
    } catch {
      /* ignore — local state is cleared regardless */
    }
    // Drop the in-memory query cache so the next account to sign in on this
    // device never sees the previous user's data. On web the reload below also
    // wipes it, but native has no reload, so clear explicitly.
    queryClient.clear();
    if (isNative) {
      // Drop back to MobileWelcome (renders when !authenticated).
      setUser(null);
      return;
    }
    // Reload so the SPA re-bootstraps unauthenticated: clears any cached
    // data and pops the sign-in prompt, all on-origin.
    setUser(null);
    window.location.reload();
  };

  // Re-fetch the current user (e.g. after profile-edit so topbar updates).
  // No-op in demo mode to keep the fixture user pinned.
  const refreshUser = async () => {
    if (demo) return;
    try {
      const u = await authApi.me();
      setUser(u);
    } catch {
      /* leave existing user in place — failure is logged by axios */
    }
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, isAuthenticated: !!user, isDemo: demo, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
